import React, { useEffect, useState } from 'react'
import { Navigate } from 'react-router-dom'
import {
  CAlert, CButton, CCard, CCardBody, CCol, CContainer, CForm,
  CFormInput, CFormLabel, CRow, CSpinner,
} from '@coreui/react'
import { api, currentUser, wizardPaso1, wizardPaso2, wizardPaso3, wizardPaso4 } from '../api/client'

/**
 * Wizard de configuración inicial (parametrosiniciales de SiprinMax).
 * Pasos: 0=BIENVENIDO, 1=SOCIO, 2=CAJA, 3=CAPITAL, 4=RUTA, 5=COMPLETADO.
 * Mismo diseño que login/registro. Consume la API existente de parametros-iniciales.
 */
const ParametrosInicialesPage: React.FC = () => {
  const user = currentUser()
  const [paso, setPaso] = useState(0)

  // Persistencia del wizard (pedido Rafael 2026-08-26 — misma lógica del
  // wizard GX parametrosiniciales.cs: los pasos 1-4 son socio/caja/capital/
  // ruta y el estado se deriva de los datos — crearsocioasistente.cs:173,
  // crearcajaasistente.cs:161, crearcapitalasistente.cs:212,
  // crearrutaasistente.cs:272): al montar se consulta el estado REAL
  // (GET /parametros-iniciales/estado) y se retoma en el paso pendiente
  // (socio → 2, +caja → 3, +capital → 4, +ruta → 5 completado).
  useEffect(() => {
    if (!user?.companyId) return
    api.get<any>(`/parametros-iniciales/estado?empresa=${user.companyId}`)
      .then((est: any) => { if (est?.paso) setPaso(est.paso) })
      .catch(() => undefined)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
  const [socioNombre, setSocioNombre] = useState('')
  const [cajaNombre, setCajaNombre] = useState('')
  const [valorCapital, setValorCapital] = useState('')
  const [zonaNombre, setZonaNombre] = useState('')
  const [socioCodigo, setSocioCodigo] = useState<number | null>(null)
  const [cajaNro, setCajaNro] = useState<number | null>(null)
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)

  if (!user) return <Navigate to="/login" replace />
  const empresaCodigo = user.companyId ?? ''

  const handlePaso1 = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setLoading(true)
    try {
      const res = await wizardPaso1(empresaCodigo, socioNombre)
      setSocioCodigo(res.socioCodigo)
      setPaso(2)
      setLoading(false)
    } catch (err: any) {
      setError(err.message ?? 'No se pudo crear el socio')
      setLoading(false)
    }
  }

  const handlePaso2 = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setLoading(true)
    try {
      const res = await wizardPaso2(empresaCodigo, cajaNombre)
      setCajaNro(res.cajaNro)
      setPaso(3)
      setLoading(false)
    } catch (err: any) {
      setError(err.message ?? 'No se pudo crear la caja')
      setLoading(false)
    }
  }

  const handlePaso3 = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setLoading(true)
    try {
      if (socioCodigo !== null && cajaNro !== null) {
        await wizardPaso3(empresaCodigo, Number(valorCapital), socioCodigo, cajaNro)
        setPaso(4)
        setLoading(false)
      }
    } catch (err: any) {
      setError(err.message ?? 'No se pudo registrar el capital inicial')
      setLoading(false)
    }
  }

  const handlePaso4 = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setLoading(true)
    try {
      await wizardPaso4(empresaCodigo, zonaNombre)
      setPaso(5)
      setLoading(false)
    } catch (err: any) {
      setError(err.message ?? 'No se pudo crear la ruta')
      setLoading(false)
    }
  }

  const button = (label: string) => (
    <CButton color="primary" type="submit" disabled={loading} className="w-100">
      {loading ? <CSpinner size="sm" /> : label}
    </CButton>
  )

  return (
    <div className="bg-body-tertiary min-vh-100 d-flex flex-row align-items-center">
      <CContainer>
        <CRow className="justify-content-center">
          <CCol md={8} lg={6} xl={5}>
            <div className="d-flex flex-column gap-4">
              <div className="text-center">
                <img src="/logo_siprinia.png" alt="SiprinIA" height={72} className="mx-auto d-block" />
              </div>
              <CCard className="p-4">
                <CCardBody className="d-flex flex-column gap-4">
                  {paso === 0 && (
                    <>
                      <h2 className="h5 text-center mb-0">Configuración inicial</h2>
                      <p className="text-body-secondary text-center mb-0">
                        Bienvenido a SiprinIA. Antes de operar necesitamos configurar tu empresa:
                        socio, caja, capital inicial y tu primera ruta.
                      </p>
                      <CButton color="primary" className="w-100" onClick={() => setPaso(1)}>
                        Comenzar
                      </CButton>
                    </>
                  )}

                  {paso === 1 && (
                    <>
                      <small className="text-body-secondary text-center">Paso 1 de 4</small>
                      <h2 className="h5 text-center mb-0">Paso 1 de 4 — Socio</h2>
                      {error && <CAlert color="danger" className="mb-0">{error}</CAlert>}
                      <CForm onSubmit={handlePaso1}>
                        <div className="d-flex flex-column gap-4">
                          <CFormLabel htmlFor="wiz-socio">Socio Nombre</CFormLabel>
                          <CFormInput
                            id="wiz-socio"
                            type="text"
                            placeholder="Nombre Socio"
                            value={socioNombre}
                            onChange={(e) => setSocioNombre(e.target.value.toUpperCase())}
                            required
                          />
                          {button('Continuar')}
                        </div>
                      </CForm>
                    </>
                  )}

                  {paso === 2 && (
                    <>
                      <small className="text-body-secondary text-center">Paso 2 de 4</small>
                      <h2 className="h5 text-center mb-0">Paso 2 de 4 — Caja</h2>
                      <p className="text-center text-body-secondary mb-0">Crear Caja, puede usar una caja para todas las rutas o luego crear caja por cada ruta</p>
                      {error && <CAlert color="danger" className="mb-0">{error}</CAlert>}
                      <CForm onSubmit={handlePaso2}>
                        <div className="d-flex flex-column gap-4">
                          <CFormLabel htmlFor="wiz-caja">Caja Nombre</CFormLabel>
                          <CFormInput
                            id="wiz-caja"
                            type="text"
                            placeholder="Ej: Caja 1"
                            value={cajaNombre}
                            onChange={(e) => setCajaNombre(e.target.value.toUpperCase())}
                            required
                          />
                          {button('Continuar')}
                        </div>
                      </CForm>
                    </>
                  )}

                  {paso === 3 && (
                    <>
                      <small className="text-body-secondary text-center">Paso 3 de 4</small>
                      <h2 className="h5 text-center mb-0">Paso 3 de 4 — Capital inicial</h2>
                      <p className="text-center text-body-secondary mb-0">Ingrese el Capital Inicial, Este valor puede ser modificado Luego</p>
                      {error && <CAlert color="danger" className="mb-0">{error}</CAlert>}
                      <CForm onSubmit={handlePaso3}>
                        <div className="d-flex flex-column gap-4">
                          <CFormLabel htmlFor="wiz-capital">Ingresos Valor</CFormLabel>
                          <CFormInput
                            id="wiz-capital"
                            type="number"
                            min={0}
                            placeholder="Ingrese un Valor"
                            value={valorCapital}
                            onChange={(e) => setValorCapital(e.target.value)}
                            required
                          />
                          {button('Continuar')}
                        </div>
                      </CForm>
                    </>
                  )}

                  {paso === 4 && (
                    <>
                      <small className="text-body-secondary text-center">Paso 4 de 4</small>
                      <h2 className="h5 text-center mb-0">Paso 4 de 4 — Ruta</h2>
                      {error && <CAlert color="danger" className="mb-0">{error}</CAlert>}
                      <CForm onSubmit={handlePaso4}>
                        <div className="d-flex flex-column gap-4">
                          <CFormLabel htmlFor="wiz-ruta">Ruta Nombre</CFormLabel>
                          <CFormInput
                            id="wiz-ruta"
                            type="text"
                            placeholder="Ej: Ruta 1"
                            value={zonaNombre}
                            onChange={(e) => setZonaNombre(e.target.value.toUpperCase())}
                            required
                          />
                          {button('Finalizar')}
                        </div>
                      </CForm>
                    </>
                  )}

                  {paso === 5 && (
                    <>
                      <h2 className="h5 text-center mb-0">¡Configuración completada!</h2>
                      <p className="text-body-secondary text-center mb-0">
                        Tu empresa quedó configurada: socio, caja, capital y ruta creados.
                      </p>
                      <CButton color="primary" className="w-100" onClick={() => { window.location.href = '/home' }}>
                        Ir al inicio
                      </CButton>
                    </>
                  )}
                </CCardBody>
              </CCard>
            </div>
          </CCol>
        </CRow>
      </CContainer>
    </div>
  )
}

export default ParametrosInicialesPage
